import React, { useState, useRef } from "react"
import extDB from "./extensions.json"
import Button from "aldoo-ra/Button"
import VariantReader from "aldoo-ra/VariantReader"
import Typography from "aldoo-ra/Typography"
import { twMerge } from "tailwind-merge"

// Function to look up MIME types from file extensions
const mimeLookUp = (ext) => {
  return extDB[ext] || ""
}

/**
 * FileUpload component for handling file uploads with validation
 * @param {Object} props - Component props
 * @param {string} props.label - Label text for the file input
 * @param {Function} props.onFileChange - Callback function when file selection changes
 * @param {Array} props.formats - Allowed file formats as extensions (e.g. ['pdf', 'docx'])
 * @param {number} props.maxSize - Maximum file size in MB
 * @param {string} props.error - Error message to display
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.multiple - Allow multiple file selection
 * @param {boolean} props.showSelectedFiles - Show names of selected files
 * @param {Function} props.onError - Callback function for validation errors
 * @param {string} props.variant - UI variant to use
 * @param {Object} props.styleOverrides - Override styles for the component
 * @returns {JSX.Element} The FileUpload component
 */
export default function FileUpload({
  label,
  onFileChange,
  formats = [],
  maxSize = 5,
  error = "",
  className = "",
  multiple = false,
  showSelectedFiles = true,
  onError,
  variant = "default",
  styleOverrides = {},
}) {
  const [fileNames, setFileNames] = useState([])
  const fileInputRef = useRef(null)

  // Get styles from variant system
  const variants = VariantReader.useVariant({
    componentName: "FileUpload",
    variant,
    styleOverrides,
  })

  // Convert formats from extensions to MIME types
  const allowedMimeTypes = formats
    .map((ext) => mimeLookUp(ext))
    .filter((type) => type)

  const validate = (file) => {
    const ext = file.name.split(".").pop().toLowerCase()
    const mimeType = mimeLookUp(ext)

    // Check if the file is of the correct format by checking MIME types
    const isValidFormat =
      allowedMimeTypes.length === 0 || allowedMimeTypes.includes(mimeType)
    if (!isValidFormat) {
      const allowedExtensions = formats.join(", ")
      return `Invalid file type. Allowed types are: ${allowedExtensions}`
    }

    const maxSizeInBytes = maxSize * 1024 * 1024
    // Check file size
    if (file.size > maxSizeInBytes) {
      return `${file.name} exceeds the limit of ${maxSize} MB`
    }

    return ""
  }

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files)
    if (files.length === 0) return

    // Validate the files
    const errors = files.map(validate).filter((error) => error)
    if (errors.length > 0) {
      onError && onError(errors)
      return
    }

    // Clear the error messages
    onError && onError([])

    // Update state with selected file names
    setFileNames(files.map((file) => file.name))
    onFileChange(multiple ? files : files[0])
    e.target.value = null // Reset the input
  }

  // Trigger file input click when the button is clicked
  const handleClick = () => {
    fileInputRef.current.click()
  }

  return (
    <div className={twMerge(variants.root, className)}>
      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept={
          allowedMimeTypes.length > 0 ? allowedMimeTypes.join(",") : "*/*"
        } // Use MIME types for accept
        multiple={multiple}
      />
      <div className={variants.container}>
        {/* Add Button */}
        <Button
          variant={variant.includes("admin") ? "admin_secondary" : "secondary"}
          onClick={handleClick}
          className={variants.button}
        >
          {multiple ? "Select Files" : "Select File"}
        </Button>

        {/* Label */}
        {label && (
          <Typography 
            variant={variant.includes("admin") ? "admin_txt_accent" : "txt_accent"} 
            className={variants.label}
          >
            {label}
          </Typography>
        )}
        
        {/* Display Selected File Names */}
        {showSelectedFiles && fileNames.length > 0 && (
          <Typography 
            variant={variant.includes("admin") ? "admin_txt1" : "txt1"} 
            className={variants.fileNames}
          >
            {fileNames.join(", ")}
          </Typography>
        )}
      </div>
      
      {/* Error Message */}
      {error && (
        <Typography 
          variant={variant.includes("admin") ? "admin_txt_error" : "txt_error"} 
          className={variants.error}
        >
          {error}
        </Typography>
      )}
    </div>
  )
}